'use client';

import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import type { PanelFooterProps } from '@/components/panels/panelFooter';
import { Loader } from '@/components/shared/loader';

/**
 * In-body progress row shown while a step's check runs, in place of the step's status alerts. The
 * footer buttons stay visible (disabled) rather than being swapped for a footer spinner, so the
 * dialog's layout holds still during the check.
 */
export const CheckingIndicator = t0 => {
  const $ = _c(4);
  const {
    text
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "12px",
      alignItems: "center"
    };
    t2 = <Loader size="medium" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== text) {
    t3 = <Stack direction="row" sx={t1}>{t2}<Typography variant="body1">{text}</Typography></Stack>;
    $[2] = text;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};

/**
 * Footer buttons shared by the CDC onboarding step dialogs: "Check & Continue" until the step
 * passes; "Verify again" + "Continue" once it does. A passing check is a snapshot, not a permanent
 * state — the customer can undo the setup after it — so re-checking stays reachable when the step
 * reads as done.
 */
export const stepFooterButtons = ({
  isDone,
  isBusy,
  testIdPrefix,
  onContinue,
  onCheckAndContinue,
  onVerifyAgain
}: {
  isDone: boolean;
  isBusy: boolean;
  testIdPrefix: string;
  onContinue: () => void;
  onCheckAndContinue: () => void;
  onVerifyAgain: () => void;
}): Pick<PanelFooterProps, 'primaryButton' | 'secondaryButton'> => ({
  primaryButton: isDone ? {
    text: 'Continue',
    onClick: onContinue,
    disabled: isBusy,
    'data-testid': `${testIdPrefix}-check-button`
  } : {
    text: 'Check & Continue',
    onClick: onCheckAndContinue,
    disabled: isBusy,
    'data-testid': `${testIdPrefix}-check-button`
  },
  secondaryButton: isDone ? {
    text: 'Verify again',
    onClick: onVerifyAgain,
    variant: 'outlined',
    disabled: isBusy,
    'data-testid': `${testIdPrefix}-reverify-button`
  } : undefined
});